/**
 * ============================================================================
 * ANDROID BACK BUTTON HANDLER - CRITICAL COMPONENT
 * ============================================================================
 *
 * PURPOSE:
 *   Handles Android hardware back button presses with the following behavior:
 *   - Close keyboard if open
 *   - Close active modal if present
 *   - On NON-ROOT pages: Navigate back through browser history
 *   - On ROOT pages (/, /home): Show toast on first press, close app on second
 *   - allowAppExit=false config: Disables exit, shows warning toast instead
 *
 * IMPLEMENTATION DETAILS:
 *   1. NATIVE LAYER (Android):
 *      - File: android/app/src/main/java/app/lovable/spesasemplice/MainActivity.java
 *      - Uses OnBackPressedCallback to intercept hardware back button
 *      - Dispatches JavaScript 'backbutton' event to webview via getBridge().eval()
 *      - Exposes exitApp() method via @JavascriptInterface for app closure
 *      - Prevents default behavior to avoid immediate app close
 *
 *   2. JAVASCRIPT LAYER (This file):
 *      - Listens for 'backbutton' event dispatched from MainActivity
 *      - Implements double-back-to-exit only on root paths
 *      - Uses canNavigateBack() to check browser history state
 *      - Respects allowAppExit configuration prop
 *      - On second back press at root: Calls (window as any).android.exitApp()
 *
 * KEY WORKAROUND:
 *   The Capacitor App plugin was broken (UNIMPLEMENTED) in the native layer.
 *   Instead of using App.addListener('backButton'), we:
 *   - Listen to custom 'backbutton' event from MainActivity
 *   - MainActivity dispatches this event via getBridge().eval()
 *   - For app exit: Call android.exitApp() JavaScript interface method
 *   - This completely bypasses the broken Capacitor plugin
 *
 * EXIT MECHANISM (android.exitApp):
 *   When Capacitor App plugin is unavailable:
 *   1. Second back press on root path detected
 *   2. Code checks: typeof (window as any).android?.exitApp === 'function'
 *   3. If available: (window as any).android.exitApp()
 *   4. Native Java method executes finishAndRemoveTask()
 *   5. Activity finishes cleanly, app closes
 *
 *   This android object is exposed by MainActivity via:
 *   getBridge().getWebView().addJavascriptInterface(this, "android")
 *   And the exitApp() method is marked with @JavascriptInterface annotation
 *
 * ⚠️ CRITICAL REFERENCES:
 *   If back button behavior breaks in future, check:
 *   1. android/app/src/main/java/app/lovable/spesasemplice/MainActivity.java
 *      - Verify OnBackPressedCallback is registered
 *      - Verify getBridge().eval("document.dispatchEvent(new Event('backbutton'));", null)
 *      - Verify getBridge().getWebView().addJavascriptInterface(this, "android")
 *      - Verify exitApp() method exists with @JavascriptInterface annotation
 *
 *   2. src/hooks/useBackButtonHandler.ts (this file)
 *      - Verify setupNativeBackButtonHandler() is called in useEffect
 *      - Verify 'backbutton' event listener is registered
 *      - Check ROOT_PATHS configuration matches expected root pages
 *      - Verify exit logic calls (window as any).android.exitApp()
 *
 *   3. src/App.tsx
 *      - Verify <BackButtonHandler allowAppExit={false} rootPaths={["/", "/home"]} />
 *      - Props configure the double-back behavior
 *
 * FLOW DIAGRAM (NORMAL BACK):
 *   User presses back button (Android hardware)
 *        ↓
 *   MainActivity.OnBackPressedCallback triggered
 *        ↓
 *   getBridge().eval() dispatches 'backbutton' event to JS
 *        ↓
 *   setupNativeBackButtonHandler() listener catches event
 *        ↓
 *   handleBackButtonPress() checks:
 *      - Is keyboard open? → Close it (hideKeyboard)
 *      - Is modal open? → Close it (modalStore.close)
 *      - Is root path? → Show double-back-to-exit logic
 *      - Not root? → Navigate back (navigate(-1))
 *
 * FLOW DIAGRAM (EXIT ON DOUBLE BACK):
 *   First back press on root + allowAppExit=false
 *        ↓
 *   Show toast: "Premi di nuovo per uscire"
 *   Set backPressedOnce.current = true
 *   Start 2-second timeout to reset flag
 *        ↓
 *   Second back press within 2 seconds
 *        ↓
 *   wasDoublePress = true
 *   Check: isAppPluginAvailable?
 *      - YES: Call App.exitApp() (Capacitor method)
 *      - NO: Call (window as any).android.exitApp() (JavaScript interface)
 *        ↓
 *   Java method executes finishAndRemoveTask()
 *        ↓
 *   Activity finishes, app closes
 *
 * ============================================================================
 */

import { useEffect, useRef, useCallback, useState, createElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { App, type BackButtonListenerEvent } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { useModalStore } from "@/services/modalStore";
import { ROOT_PATHS } from "@/config/routes";
import { visualLog } from "@/components/debug/VisualDebugger";
import { useKeyboardState } from "@/hooks/useKeyboardState";
import type {
  BackLogger,
  BackButtonAction,
  BackButtonLogPayload
} from "@/services/backLogger";

const canNavigateBack = () => {
  try {
    // Fallback 1: Controlla history.state (React Router pattern)
    const state = window.history.state as { idx?: number } | null;
    if (typeof state?.idx === 'number' && state.idx > 0) {
      return true;
    }

    // Fallback 2: Controlla history.length (Standard browser API)
    // history.length > 1 significa che l'utente ha visitato almeno una pagina prima
    if (window.history.length > 1) {
      return true;
    }

    return false;
  } catch (e) {
    visualLog.error(`canNavigateBack() failed: ${e}`);
    return false;
  }
};

type CapacitorPlatformStatus = "native" | "web" | "unknown";

const resolveCapacitorPlatformStatus = (): CapacitorPlatformStatus => {
  const windowCapacitor =
    typeof window !== 'undefined'
      ? (window as unknown as { Capacitor?: typeof Capacitor }).Capacitor
      : undefined;

  const nativeCheck = (() => {
    if (typeof Capacitor?.isNativePlatform === 'function') {
      return Capacitor.isNativePlatform();
    }

    if (typeof windowCapacitor?.isNativePlatform === 'function') {
      return windowCapacitor.isNativePlatform();
    }

    return undefined;
  })();

  if (typeof nativeCheck === 'boolean') {
    return nativeCheck ? 'native' : 'web';
  }

  const platformCheck = (() => {
    if (typeof Capacitor?.getPlatform === 'function') {
      return Capacitor.getPlatform();
    }

    if (typeof windowCapacitor?.getPlatform === 'function') {
      return windowCapacitor.getPlatform();
    }

    return undefined;
  })();

  if (typeof platformCheck === 'string') {
    return platformCheck === 'web' ? 'web' : 'native';
  }

  return 'unknown';
};

export const isCapacitorPlatform = () => {
  return resolveCapacitorPlatformStatus() === 'native';
};

export const isRoot = (path: string, customRoots?: string[]) => {
  const roots = customRoots || ROOT_PATHS;
  const normalizedPath = path.split('?')[0].replace(/\/+$/, '') || '/';
  return roots.some(r => {
    const normalizedRoot = r.split('?')[0].replace(/\/+$/, '') || '/';
    return (
      normalizedRoot === normalizedPath ||
      normalizedRoot === `/${normalizedPath}` ||
      `/${normalizedRoot}` === normalizedPath
    );
  });
};

const EXIT_HINT_TOAST_ID = 'back-button-exit-hint';

export interface UseBackButtonHandlerOptions {
  rootPaths?: string[];
  exitMessage?: string;
  logger?: BackLogger;
  allowAppExit?: boolean;
}

export const useBackButtonHandler = (
  {
    rootPaths,
    exitMessage = 'Premi di nuovo per uscire',
    logger,
    allowAppExit = true,
  }: UseBackButtonHandlerOptions = {}
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const modalStore = useModalStore();
  const { isKeyboardOpen, hideKeyboard } = useKeyboardState();
  const loggerRef = useRef<BackLogger | undefined>(logger);

  const backPressedOnce = useRef(false);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const listenerRef = useRef<PluginListenerHandle | null>(null);
  const fallbackRegisteredRef = useRef(false);
  const [isAppPluginAvailable, setIsAppPluginAvailable] = useState(
    () => Capacitor.isPluginAvailable?.("App") ?? false
  );
  const lastActionTime = useRef<number>(0);
  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);
  const debounceTime = 300; // ms per evitare doppi tap

  useEffect(() => {
    loggerRef.current = logger;
  }, [logger]);

  const handleBackButtonPress = useCallback(async (event?: BackButtonListenerEvent) => {
    const currentPath = pathRef.current;
    const now = Date.now();

    if (now - lastActionTime.current < debounceTime) {
      visualLog.info(`Back button debounced (${now - lastActionTime.current}ms)`);
      return;
    }

    lastActionTime.current = now;

    visualLog.info(
      `Back button pressed. Path: ${currentPath}, ActiveModal: ${modalStore.activeModal}, KeyboardOpen: ${isKeyboardOpen}`
    );

    const startTime =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    const wasDoublePress = backPressedOnce.current;
    const activeModal = modalStore.activeModal ?? null;
    const keyboardWasOpen = isKeyboardOpen;
    const eventCanGoBack = typeof event?.canGoBack === 'boolean' ? event.canGoBack : null;
    const clientCanGoBack = canNavigateBack();

    // Prefer the client-side history check when it reports a back entry exists.
    // Some platforms may send event.canGoBack=false or omit it; relying solely
    // on the native event caused treating every route as root and exiting the app.
    // If the client history indicates a back is possible, trust it. Otherwise
    // fall back to the event value.
    const effectiveCanGoBack = clientCanGoBack || (eventCanGoBack ?? false);

    const goBackSource: 'event' | 'history' | 'history-override' = (() => {
      if (eventCanGoBack === null) {
        return 'history';
      }

      if (eventCanGoBack) {
        return 'event';
      }

      if (clientCanGoBack) {
        return 'history-override';
      }

      return 'history';
    })();
    const historyLength =
      typeof window !== 'undefined' && typeof window.history?.length === 'number'
        ? window.history.length
        : 0;

    const logAction = async (
      action: BackButtonAction,
      metadata?: Record<string, unknown>,
      overrides?: Partial<Pick<BackButtonLogPayload, 'modal' | 'isKeyboardOpen' | 'doublePress'>>
    ) => {
      const endTime =
        typeof performance !== 'undefined' && typeof performance.now === 'function'
          ? performance.now()
          : Date.now();

      const payload: BackButtonLogPayload = {
        action,
        route: currentPath,
        modal: overrides?.modal ?? activeModal,
        isKeyboardOpen: overrides?.isKeyboardOpen ?? keyboardWasOpen,
        canGoBack: {
          event: eventCanGoBack,
          client: clientCanGoBack,
        },
        historyLength,
        doublePress: overrides?.doublePress ?? wasDoublePress,
        durationMs: endTime - startTime,
        metadata,
      };

      try {
        await loggerRef.current?.(payload);
      } catch (error) {
        visualLog.error?.(`Error invoking back logger: ${error}`);
      }
    };

    try {
      if (keyboardWasOpen) {
        visualLog.info("Keyboard is open, hiding it");
        hideKeyboard();
        void logAction('hide-keyboard');
        return;
      }

      if (modalStore.hasActiveModal?.() || activeModal) {
        visualLog.info(`Closing modal ${activeModal} via back button`);
        modalStore.resetModals?.();
        void logAction('close-modal', activeModal ? { modalId: activeModal } : undefined, {
          modal: activeModal,
        });
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      if (currentPath === '/shopping' && searchParams.has('list')) {
        visualLog.info('Back from shopping list, navigating to /lists');
        navigate('/lists');
        void logAction('navigate-to-lists', {
          target: '/lists',
          query: location.search,
        });
        return;
      }

      if (isRoot(currentPath, rootPaths)) {
        // On root paths, ALWAYS enforce double-tap-to-exit.
        // Do NOT navigate back based on browser history, even if it exists:
        // the double-tap pattern ensures users don't accidentally exit the app.
        if (!wasDoublePress) {
          backPressedOnce.current = true;
          toast.custom(
            () =>
              createElement(
                'div',
                {
                  role: 'status',
                  'aria-live': 'assertive',
                  'aria-atomic': 'true',
                  className:
                    'rounded-md bg-neutral-900 text-white px-4 py-2 text-sm shadow-lg',
                },
                exitMessage
              ),
            { id: EXIT_HINT_TOAST_ID, duration: 2000 }
          );
          visualLog.info("First back press on root path, showing exit message");

          if (timeout.current) clearTimeout(timeout.current);
          timeout.current = setTimeout(() => {
            backPressedOnce.current = false;
          }, 2000);

          void logAction('show-exit-hint', { message: exitMessage });
          return;
        }

        visualLog.info("Second back press on root path, attempting app exit");

        // Clear the timeout and flag immediately when we reach the second press
        if (timeout.current) {
          clearTimeout(timeout.current);
          timeout.current = null;
        }
        backPressedOnce.current = false;

        // NOTE: allowAppExit only controls the FIRST back press behavior (showing
        // the toast). On the SECOND back press we ALWAYS attempt to exit the app,
        // so double-tap-to-exit works consistently.
        if (isAppPluginAvailable) {
          await logAction('exit-app');
          try {
            // Wrap in try/catch for synchronous errors
            const exitPromise = App.exitApp();
            // Also add .catch() for async errors (Promise rejections)
            if (exitPromise && typeof exitPromise.catch === 'function') {
              exitPromise.catch((error: any) => {
                visualLog.error(`App.exitApp() failed (async): ${error}`);
                // Fallback to native exit
                if (typeof (window as any).android !== 'undefined' &&
                    typeof (window as any).android.exitApp === 'function') {
                  try {
                    (window as any).android.exitApp();
                  } catch (nativeError) {
                    visualLog.error(`Native exit also failed: ${nativeError}`);
                  }
                }
              });
            }
          } catch (error) {
            visualLog.error(`App.exitApp() failed (sync): ${error}`);
            // Fallback to native exit
            if (typeof (window as any).android !== 'undefined' &&
                typeof (window as any).android.exitApp === 'function') {
              try {
                (window as any).android.exitApp();
              } catch (nativeError) {
                visualLog.error(`Native exit also failed: ${nativeError}`);
              }
            }
          }
        } else {
          // Call native exitApp() method via JavaScript interface exposed by
          // MainActivity.java via addJavascriptInterface(this, "android").
          // See: android/app/src/main/java/app/lovable/spesasemplice/MainActivity.java
          if (typeof (window as any).android !== 'undefined' &&
              typeof (window as any).android.exitApp === 'function') {
            try {
              (window as any).android.exitApp();
              void logAction('native-exit');
            } catch (error) {
              visualLog.error(`Failed to call native exit: ${error}`);
              if (window.history.length > 1) {
                window.history.back();
                void logAction('history-fallback');
              }
            }
          } else {
            if (window.history.length > 1) {
              window.history.back();
              void logAction('history-fallback');
            } else {
              void logAction('history-unavailable');
            }
          }
        }
      } else {
        visualLog.info(`Navigating back from ${currentPath}`);
        if (effectiveCanGoBack) {
          navigate(-1);
          void logAction('navigate-back', {
            source: goBackSource,
          });
        } else {
          const fallback = rootPaths?.[0] || '/';
          visualLog.info(`No history, navigating to ${fallback}`);
          navigate(fallback, { replace: true });
          void logAction('navigate-fallback', { fallback });
        }
      }
    } catch (error) {
      visualLog.error(`Error handling back button: ${error}`);
      void logAction('error', {
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, [
    modalStore,
    navigate,
    exitMessage,
    isKeyboardOpen,
    hideKeyboard,
    rootPaths,
    location.search,
    isAppPluginAvailable,
    allowAppExit,
  ]);

  useEffect(() => {
    backPressedOnce.current = false;
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }
    if (modalStore.hasActiveModal()) {
      visualLog.info('Clearing active modal on path change');
      modalStore.resetModals();
    }
  }, [location.pathname]);

  useEffect(() => {
    let isActive = true;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;
    let retryAttempts = 0;
    let hasRegistered = false;
    let documentBackListener: ((event: Event) => void) | null = null;

    const clearRetry = () => {
      if (retryHandle) {
        clearTimeout(retryHandle);
        retryHandle = null;
      }
    };

    const scheduleRetry = (
      reason:
        | 'platform-unavailable'
        | 'plugin-unavailable'
        | 'registration-error'
    ) => {
      if (!isActive || hasRegistered || fallbackRegisteredRef.current) {
        return;
      }

      clearRetry();
      retryAttempts += 1;
      const shouldLogRetry =
        retryAttempts === 1 || retryAttempts % 5 === 0;

      if (shouldLogRetry) {
        visualLog.info(
          `Retrying native back listener registration (#${retryAttempts}) due to ${reason}`
        );
      }
      retryHandle = setTimeout(() => {
        retryHandle = null;
        void ensureListener();
      }, 250);
    };

    const setupNativeBackButtonHandler = () => {
      /**
       * Sets up the back button handler by listening to the custom 'backbutton'
       * event dispatched by MainActivity.java, bypassing the broken Capacitor
       * App.addListener('backButton') plugin (which throws UNIMPLEMENTED).
       *
       * REFERENCE: android/app/src/main/java/app/lovable/spesasemplice/MainActivity.java
       */
      try {
        documentBackListener = () => {
          const backEvent: BackButtonListenerEvent = { canGoBack: canNavigateBack() };
          void handleBackButtonPress(backEvent);
        };
        document.addEventListener('backbutton', documentBackListener);
        visualLog.info("Native back button handler registered via 'backbutton' event");
        return true;
      } catch (error) {
        visualLog.error(`Failed to setup native back button: ${error}`);
        return false;
      }
    };

    const ensureListener = async () => {
      if (!isActive || hasRegistered) {
        return;
      }

      const platformStatus = resolveCapacitorPlatformStatus();

      if (platformStatus !== 'native') {
        if (isActive) {
          setIsAppPluginAvailable(false);
        }
        scheduleRetry('platform-unavailable');
        return;
      }

      try {
        // FIRST: Try native back button handler (bypass broken App plugin)
        const nativeSetupSuccess = setupNativeBackButtonHandler();
        if (nativeSetupSuccess) {
          hasRegistered = true;
          if (isActive) {
            setIsAppPluginAvailable(true);
          }
          if (!fallbackRegisteredRef.current) {
            fallbackRegisteredRef.current = true;
          }
          clearRetry();
          return;
        }

        // SECOND: Fall back to the Capacitor App plugin when available
        const hasAppPlugin = !!(App && typeof App.addListener === "function");

        if (!hasAppPlugin) {
          if (isActive) {
            setIsAppPluginAvailable(false);
          }

          if (!fallbackRegisteredRef.current) {
            fallbackRegisteredRef.current = true;
            visualLog.info("Capacitor App plugin unavailable, using popstate fallback");
          }

          hasRegistered = true;
          clearRetry();
          return;
        }

        if (listenerRef.current) {
          try {
            await listenerRef.current.remove();
          } catch (error) {
            visualLog.error(`Error removing existing native back button listener: ${error}`);
          }
        }

        try {
          listenerRef.current = await App.addListener("backButton", event => {
            void handleBackButtonPress(event);
          });
          visualLog.info("Native back button listener registered");
          hasRegistered = true;
          retryAttempts = 0;
          clearRetry();
          fallbackRegisteredRef.current = false;
          if (isActive) {
            setIsAppPluginAvailable(true);
          }
        } catch (addListenerError) {
          // The error "App plugin is not implemented on android" is expected and
          // is handled gracefully (popstate fallback) without reporting to Sentry.
          if (isActive) {
            setIsAppPluginAvailable(false);
          }
          if (!fallbackRegisteredRef.current) {
            fallbackRegisteredRef.current = true;
            visualLog.info("Capacitor App plugin unavailable, using popstate fallback");
          }
          hasRegistered = true;
          clearRetry();
        }
      } catch (error) {
        visualLog.error(`Failed to register native back button listener: ${error}`);
        if (isActive) {
          setIsAppPluginAvailable(false);
        }
        scheduleRetry("registration-error");
      }
    };

    void ensureListener();

    return () => {
      isActive = false;
      clearRetry();

      if (documentBackListener) {
        document.removeEventListener('backbutton', documentBackListener);
        documentBackListener = null;
      }

      const removeListener = listenerRef.current;
      listenerRef.current = null;
      fallbackRegisteredRef.current = false;

      if (removeListener) {
        removeListener
          .remove()
          .then(() => {
            visualLog.info("Native back button listener removed");
          })
          .catch(error => {
            visualLog.error(`Error removing native back button listener: ${error}`);
          });
      }

      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, [handleBackButtonPress]);

  useEffect(() => {
    const nativePlatform = isCapacitorPlatform();

    if (nativePlatform && isAppPluginAvailable) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      if (modalStore.hasActiveModal()) {
        event.preventDefault();
        event.stopPropagation();
        modalStore.resetModals();
        navigate(1);
        return;
      }

      backPressedOnce.current = false;
    };

    window.addEventListener("popstate", handlePopState);

    if (nativePlatform && !isAppPluginAvailable) {
      visualLog.info(
        "Registered popstate fallback for missing Capacitor App plugin"
      );
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, [modalStore, navigate, isAppPluginAvailable]);

  return { handleBackButtonPress };
};
